import { sqliteTable, text, real, integer } from 'drizzle-orm/sqlite-core';

export const profiles = sqliteTable('profiles', {
  id: text('id').primaryKey(),
  hourlyRate: real('hourly_rate').notNull().default(0),
  defaultMarkupPct: real('default_markup_pct').notNull().default(0),
  locale: text('locale').notNull().default('pt-BR'),
  updatedAt: integer('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
});

export const projects = sqliteTable('projects', {
  id: text('id').primaryKey(),
  userId: text('user_id').notNull(),
  name: text('name').notNull(),
  status: text('status', { enum: ['not_started', 'in_progress', 'finished', 'cancelled'] })
    .notNull()
    .default('not_started'),
  recipeUrl: text('recipe_url'),
  notes: text('notes'),
  hourlyRateOverride: real('hourly_rate_override'),
  markupPctOverride: real('markup_pct_override'),
  extraCosts: real('extra_costs').notNull().default(0),
  saleFeePct: real('sale_fee_pct').notNull().default(0),
  /** Preço praticado (spec S04, Etapa 13): campo INFORMATIVO preenchido pela
   * usuária ao marcar o projeto como finalizado — NUNCA entra em nenhum
   * cálculo (o preço em si continua sempre recalculado por
   * `domain/pricing.ts`, nunca armazenado; esta é a única exceção
   * deliberada da spec, documentada em CLAUDE.md). Nullable: "pular" no
   * diálogo de finalização é uma opção válida. */
  practicedPrice: real('practiced_price'),
  firstStitchAt: integer('first_stitch_at', { mode: 'timestamp' }),
  lastStitchAt: integer('last_stitch_at', { mode: 'timestamp' }),
  createdAt: integer('created_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
  updatedAt: integer('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
  deletedAt: integer('deleted_at', { mode: 'timestamp' }),
});

export const supplies = sqliteTable('supplies', {
  id: text('id').primaryKey(),
  userId: text('user_id').notNull(),
  type: text('type').notNull(),
  name: text('name').notNull(),
  unit: text('unit').notNull().default('un'),
  unitPrice: real('unit_price').notNull().default(0),
  updatedAt: integer('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
  deletedAt: integer('deleted_at', { mode: 'timestamp' }),
});

export const projectSupplies = sqliteTable('project_supplies', {
  id: text('id').primaryKey(),
  projectId: text('project_id').notNull(),
  supplyId: text('supply_id').notNull(),
  quantity: real('quantity').notNull(),
  unitPriceSnapshot: real('unit_price_snapshot').notNull(),
  updatedAt: integer('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
  deletedAt: integer('deleted_at', { mode: 'timestamp' }),
});

export const timeEntries = sqliteTable('time_entries', {
  id: text('id').primaryKey(),
  projectId: text('project_id').notNull(),
  startedAt: integer('started_at', { mode: 'timestamp' }).notNull(),
  endedAt: integer('ended_at', { mode: 'timestamp' }),
  updatedAt: integer('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
  deletedAt: integer('deleted_at', { mode: 'timestamp' }),
});

export const counters = sqliteTable('counters', {
  id: text('id').primaryKey(),
  projectId: text('project_id').notNull(),
  name: text('name').notNull().default('Carreiras'),
  isPrimary: integer('is_primary', { mode: 'boolean' }).notNull().default(false),
  updatedAt: integer('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
  deletedAt: integer('deleted_at', { mode: 'timestamp' }),
});

export const counterEvents = sqliteTable('counter_events', {
  id: text('id').primaryKey(),
  counterId: text('counter_id').notNull(),
  delta: integer('delta').notNull(), // -1 or +1
  at: integer('at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
});

export const projectPhotos = sqliteTable('project_photos', {
  id: text('id').primaryKey(),
  projectId: text('project_id').notNull(),
  storagePath: text('storage_path').notNull(),
  takenAt: integer('taken_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
  updatedAt: integer('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
  deletedAt: integer('deleted_at', { mode: 'timestamp' }),
});

export const outbox = sqliteTable('outbox', {
  id: text('id').primaryKey(),
  tableName: text('table_name').notNull(),
  recordId: text('record_id').notNull(),
  operation: text('operation', { enum: ['INSERT', 'UPDATE', 'DELETE'] }).notNull(),
  payload: text('payload', { mode: 'json' }).notNull(),
  attemptCount: integer('attempt_count').notNull().default(0),
  lastAttemptAt: integer('last_attempted_at', { mode: 'timestamp' }),
  createdAt: integer('created_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
  processedAt: integer('processed_at', { mode: 'timestamp' }),
  /** Etapa 15 (S05): 'pending' é o estado normal (getPending só devolve
   * estes); 'processed' espelha `processedAt` (mantido por compat/consulta
   * direta); 'quarantined' é um item com erro NÃO recuperável de push (4xx
   * do Postgrest — constraint, RLS etc.) — sai da fila normal para não
   * travar os itens depois dele, mas fica visível via `lastError` em vez de
   * sumir silenciosamente (bug corrigido nesta etapa). */
  status: text('status', { enum: ['pending', 'processed', 'quarantined'] }).notNull().default('pending'),
  /** Mensagem do último erro não recuperável (preenchida só quando
   * status='quarantined'). Erros de rede/transporte não chegam aqui — esses
   * incrementam `attemptCount` e tentam de novo depois, ver pushAll. */
  lastError: text('last_error'),
});

export const lastPulledAt = sqliteTable('last_pulled_at', {
  tableName: text('table_name').primaryKey(),
  pulledAt: integer('pulled_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date()),
});

export type Profile = typeof profiles.$inferSelect;
export type NewProfile = typeof profiles.$inferInsert;
export type Project = typeof projects.$inferSelect;
export type NewProject = typeof projects.$inferInsert;
export type Supply = typeof supplies.$inferSelect;
export type NewSupply = typeof supplies.$inferInsert;
export type ProjectSupply = typeof projectSupplies.$inferSelect;
export type NewProjectSupply = typeof projectSupplies.$inferInsert;
export type TimeEntry = typeof timeEntries.$inferSelect;
export type NewTimeEntry = typeof timeEntries.$inferInsert;
export type Counter = typeof counters.$inferSelect;
export type NewCounter = typeof counters.$inferInsert;
export type CounterEvent = typeof counterEvents.$inferSelect;
export type NewCounterEvent = typeof counterEvents.$inferInsert;
export type ProjectPhoto = typeof projectPhotos.$inferSelect;
export type NewProjectPhoto = typeof projectPhotos.$inferInsert;